import { useEffect, useRef } from 'react';
import './KnownIssues.scss';
import { Box, Typography, useMediaQuery } from '@mui/material';
import { useState } from 'react';
import { useTheme } from '@mui/system';
import AppAccordion from '../../../components/AppAccordion/AppAccordion';
import Issue from './Issue/Issue';
import knownIssuesImage from '../../../assets/images/knownIssuesImg.jpeg';
import useHandleAnchorClick from '../../../hooks/useHandleAnchorClick';
import { useTranslation } from 'react-i18next';
import KnownIssuesSearch from './knownIssueSearch';
import KnownIssuesFilterDropdown from './knownIssueFilter';
import KnownIssuesSortDropdown from './knownIssueSort';

interface KnownIssueEntry {
  affected_api: string | { value: string }[];
  status?: string;
  environments?: string | string[];
  ticket_id?: string;
  last_updated?: string;
  targeted_fix_date?: string;
  [key: string]: any;
}

interface KnownIssue {
  accordion: { details: string; summary: string }[];
  entries: KnownIssueEntry[];
  header: string;
}

interface StaticContent {
  title: string;
  operatingCar: string;
  link: {
    title: string;
  };
  affectedApisDropdown: {
    label: string;
    items: string[];
  };
  statuses: { [key: string]: string };
  environments: { [key: string]: string };
  filterLabel: string;
  filterDRop: string[];
  showAll: string;
  sort: string;
  issueTitle: string[];
}

const KnownIssues = () => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const { t } = useTranslation();

  const knownIssue = t('known_issues_new_content', {
    returnObjects: true
  })[0] as KnownIssue;
  const accordion = knownIssue?.accordion
    ? knownIssue.accordion[0]
    : { details: '', summary: '' };
  const staticContent = t('knownIssues', {
    returnObjects: true
  }) as StaticContent;

  // const [selectedApis, setSelectedApis] = useState<string[]>([showAllText]);
  const [liveRegionMessage, setLiveRegionMessage] = useState('');
  const [accordionExpanded, setAccordionExpanded] = useState(true);
  const [dropdownHeight, setDropdownHeight] = useState(0);

  const [searchText, setSearchText] = useState('');
  const [pendingSearchText, setPendingSearchText] = useState('');
  const defaultFliters = [staticContent.statuses.planned,staticContent.statuses.resolved,staticContent.statuses.inProgress,staticContent.statuses.underReview]
  
  const [selectedFilters, setSelectedFilters] = useState({
    affectedApis: [staticContent.showAll],
    status: [staticContent.statuses.planned,staticContent.statuses.resolved,staticContent.statuses.inProgress,staticContent.statuses.underReview],
    environments: [staticContent.showAll]
  });
  const [selectedSort, setSelectedSort] = useState('');

  const parser = new DOMParser();
  const linkContent: Document = parser.parseFromString(
    staticContent.link.title,
    'text/html'
  );
  const linkText = linkContent.body.innerHTML.replaceAll(
    'href="http',
    "target='_blank' href=\"http"
  );

  const removePTags = (str?: string): string => {
    if (!str) {
      return '';
    }
    return str.replace(/^<p>/, '').replace(/<\/p>$/, '');
  };

  const sortIssues = (issues: KnownIssueEntry[]) => {
    if (!Array.isArray(issues)) {
      return [];
    }
    const sorted = [...issues];

    const extraFirstTicketNumber = (ticketString) => {
      const match = (ticketString || '').match(/\d+/);
      return match ? parseInt(match[0],10) : 0;
    };

    switch (selectedSort) {
      case 'ticket_number_asc':
        return sorted.sort((a, b) => {
          const numA = extraFirstTicketNumber(a.ticket_id);
          const numB = extraFirstTicketNumber(b.ticket_id);
          return numA -numB;
        });
      case 'ticket_number_desc':
        return sorted.sort((a, b) => {
          const numA = extraFirstTicketNumber(a.ticket_id);
          const numB = extraFirstTicketNumber(b.ticket_id);
          return numB -numA;
        });
      case 'last_updated_asc':
        return sorted.sort((a, b) => {
          const dateA = a.last_updated ? new Date(a.last_updated).getTime() : 0;
          const dateB = b.last_updated ? new Date(b.last_updated).getTime() : 0;
          return dateA - dateB;
        });
      case 'last_updated_desc':
        return sorted.sort((a, b) => {
          const dateA = a.last_updated ? new Date(a.last_updated).getTime() : 0;
          const dateB = b.last_updated ? new Date(b.last_updated).getTime() : 0;
          return dateB - dateA;
        });
      case 'targeted_fix_date_asc':
        return sorted.sort((a, b) => {
          // Check if dates exist and are not empty
          const hasDateA =
            a.targeted_fix_date && a.targeted_fix_date.trim() !== '';
          const hasDateB =
            b.targeted_fix_date && b.targeted_fix_date.trim() !== '';

          // If both don't have dates (will show TBC), maintain original order
          if (!hasDateA && !hasDateB) return 0;

          // TBC values (missing dates) go to the end in ascending order
          if (!hasDateA) return 1;
          if (!hasDateB) return -1;

          // Both have dates, compare them
          const dateA = a.targeted_fix_date
            ? new Date(a.targeted_fix_date).getTime()
            : 0;
          const dateB = b.targeted_fix_date
            ? new Date(b.targeted_fix_date).getTime()
            : 0;
          return dateA - dateB;
        });
      case 'targeted_fix_date_desc':
        return sorted.sort((a, b) => {
          // Check if dates exist and are not empty
          const hasDateA =
            a.targeted_fix_date && a.targeted_fix_date.trim() !== '';
          const hasDateB =
            b.targeted_fix_date && b.targeted_fix_date.trim() !== '';

          // If both don't have dates (will show TBC), maintain original order
          if (!hasDateA && !hasDateB) return 0;

          // TBC values (missing dates) go to the end in descending order
          if (!hasDateA) return 1;
          if (!hasDateB) return -1;

          // Both have dates, compare them
          const dateA = new Date(a.targeted_fix_date ?? '').getTime();
          const dateB = new Date(b.targeted_fix_date ?? '').getTime();
          return dateB - dateA;
        });
      default:
        return sorted;
    }
  };

  // Helper function to get the display status (same logic as in Issue component)
  const getDisplayedStatus = (issue: KnownIssueEntry): string | undefined => {
    if (
      issue.status === staticContent.statuses.resolved &&
      issue.last_updated
    ) {
      const [year, month, day] = issue.last_updated.split('-').map(Number);
      const resolvedDate = new Date(Date.UTC(year, month - 1, day));
      const now = new Date();
      const diffDays = Math.floor(
        (now.getTime() - resolvedDate.getTime()) / (1000 * 60 * 60 * 24)
      );
      if (diffDays > 90) {
        return staticContent.statuses.archived;
      }
    }
    return issue.status;
  };

  const getFilteredIssues = () => {
    const entries = knownIssue.entries || [];

    // Filter by search text
    let filtered = entries;
    if (searchText.trim()) {
      const lowerSearch = searchText.toLowerCase();
      filtered = filtered.filter((entry) => {
        if (
          getDisplayedStatus(entry) === staticContent.statuses.archived &&
          lowerSearch.includes(staticContent.statuses.resolved.toLowerCase())
        ) {
          return false;
        }

        // Helper function to search in a specific value only
        const searchInValue = (val: any): boolean => {
          if (!val) return false;
          
          if (typeof val === 'string') {
            return val.toLowerCase().includes(lowerSearch);
          } else if (Array.isArray(val)) {
            return val.some((item) => {
              if (typeof item === 'string') {
                return item.toLowerCase().includes(lowerSearch);
              } else if (item && typeof item === 'object' && 'value' in item) {
                return typeof item.value === 'string' && item.value.toLowerCase().includes(lowerSearch);
              }
              return false;
            });
          } else if (val && typeof val === 'object' && 'value' in val) {
            return typeof val.value === 'string' && val.value.toLowerCase().includes(lowerSearch);
          }
          return false;
        };

        // Helper function to search in HTML content (extract text first)
        const searchInHtmlContent = (htmlContent: string): boolean => {
          if (!htmlContent || typeof htmlContent !== 'string') return false;
          
          // Create a temporary div to extract text content from HTML
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = htmlContent;
          const textContent = tempDiv.textContent || tempDiv.innerText || '';
          
          return textContent.toLowerCase().includes(lowerSearch);
        };

        // Define searchable fields - excluding details for now to test
        const basicSearchableFields = [
          entry.ticket_id,
          entry.affected_api,
          entry.summary,
          entry.use_case,
          entry.error_message,
          entry.workaround,
          entry.environment,
          getDisplayedStatus(entry),
          entry.ticket_id ? entry.summary : undefined
        ];

        // Search in basic fields
        const basicFieldSearch = basicSearchableFields.some((field) => searchInValue(field));

        // Search in HTML content (details) separately
        const detailsSearch = entry.details ? searchInHtmlContent(entry.details) : false;

        // Combine basic field search with details search
        const fieldSearch = basicFieldSearch || detailsSearch;

        // Search in formatted dates
        const dateFieldSearch = [
          entry.last_updated,
          entry.targeted_fix_date
        ].some((dateField) => {
          if (!dateField || typeof dateField !== 'string') {
            return lowerSearch === 'tbc';
          }
         
          // Match TBC (case-insensitive, allow spaces)
          if (dateField.trim().toLowerCase().replace(/\s+/g, '') === 'tbc' && lowerSearch.replace(/\s+/g, '') === 'tbc') return true;
          if (dateField.toLowerCase().includes('tbc') && lowerSearch.includes('tbc')) return true;

          // Search in raw date format (YYYY-MM-DD)
          if (dateField.toLowerCase().includes(lowerSearch)) return true;

          // Search in formatted date
          try {
            const [year, month, day] = dateField.split('-').map(Number);
            if (year && month && day) {
              const date = new Date(Date.UTC(year, month - 1, day));

              const enMonth = date.toLocaleString('en-US', { month: 'long' });
              const frMonth = date.toLocaleString('fr-FR', { month: 'long' });

              const formats = [
                `${enMonth} ${day}, ${year}`,
                `${day} ${enMonth} ${year}`,
                `${frMonth} ${day}, ${year}`,
                `${day} ${frMonth} ${year}`
              ];

              return formats.some(f =>
                f.toLowerCase().includes(lowerSearch)
              );
            }
          } catch (error) {
            // If date parsing fails, just continue
            console.log(error);
          }

          return false;
        });

        return fieldSearch || dateFieldSearch;
      });
    }

    // Filter by affectedApis
    if (
      selectedFilters.affectedApis.length &&
      !selectedFilters.affectedApis.includes(staticContent.showAll)
    ) {
      filtered = filtered.filter((issue) => {
        const affectedApi = issue.affected_api;

        return selectedFilters.affectedApis.some((api) => {
          if (typeof affectedApi === 'string') {
            return affectedApi.includes(api);
          } else if (Array.isArray(affectedApi)) {
            return affectedApi.some((item) => {
              if (typeof item === 'string') {
                return (item as string).includes(api);
              } else if (item && typeof item === 'object' && 'value' in item) {
                return (item as { value: string }).value.includes(api);
              }
              return false;
            });
          }
          return false;
        });
      });
    }

    // Filter by status
    if (
      selectedFilters.status.length &&
      !selectedFilters.status.includes(staticContent.showAll)
    ) {
      filtered = filtered.filter((issue) => {
        const displayedStatus = getDisplayedStatus(issue);
        return (
          displayedStatus && selectedFilters.status.includes(displayedStatus)
        );
      });
    }

    // Filter by environment
    if (
      selectedFilters.environments.length &&
      !selectedFilters.environments.includes(staticContent.showAll)
    ) {
      filtered = filtered.filter((issue) => {
        const environment = issue.environment; // Changed from environments to environment

        const isMatch = selectedFilters.environments.some((selectedEnv) => {
          if (typeof environment === 'string') {
            // Handle partial matching for environments like "Prod" matching "Production"
            const match =
              environment.toLowerCase().includes(selectedEnv.toLowerCase()) ||
              selectedEnv.toLowerCase().includes(environment.toLowerCase());
            return match;
          } else if (Array.isArray(environment)) {
            const match = environment.some((env) => {
              const envMatch =
                env.toLowerCase().includes(selectedEnv.toLowerCase()) ||
                selectedEnv.toLowerCase().includes(env.toLowerCase());
              return envMatch;
            });
            return match;
          }
          return false;
        });
        return isMatch;
      });
    }

    return sortIssues(filtered);
  };

  const handleAnchorClick = useHandleAnchorClick();

  const handleAccordionChange = (isExpanded: boolean) => {
    setAccordionExpanded(isExpanded);
  };

  const handleSortChange = (sortValue: string) => {
    setSelectedSort(sortValue);
  };

  useEffect(() => {
    let message = '';
    if (
      searchText.trim() ||
      selectedFilters.affectedApis.some(api => api !== staticContent.showAll) ||
      selectedFilters.status.some(status => status !== staticContent.showAll) ||
      selectedFilters.environments.some(env => env !== staticContent.showAll)
    ) {
      message =
        getFilteredIssues().length === 0
          ? staticContent['notFound']
          : `search result have been updated ${getFilteredIssues().length} ${staticContent['searchResult']}`;
    }
    // Delay to ensure screen readers catch the update
    const timeout = setTimeout(() => setLiveRegionMessage(message), 100);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText,selectedFilters,staticContent]);


  return (
    <Box className="known-issues">
      {isDesktop ? (
        <div className="container-header">
          <div dangerouslySetInnerHTML={{ __html: knownIssue?.header || '' }} />
        </div>
      ) : (
        <>
          <img alt="Known Issues" src={knownIssuesImage} width="100%" />
          <h1 className="title-mobile">{staticContent.title}</h1>
        </>
      )}
     <main>
      <Box className={isDesktop ? 'container' : 'container container--mobile'}>
        <AppAccordion
          details={accordion.details}
          summary={accordion.summary}
          expanded={accordionExpanded}
          onChange={handleAccordionChange}
           headingLevel="h2"
        />

        {/* <AppMultiSelect
          label={staticContent.affectedApisDropdown.label}
          itemList={staticContent.affectedApisDropdown.items}
          values={selectedApis}
          width={250}
          handleOnChange={handleOnChangeSelect}
        /> */}

        <Box
           sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
            mb: 2,
            flexWrap: 'wrap',
            [theme.breakpoints.down('md')]: {
              flexDirection: 'column',
              alignItems: 'stretch',
              gap: 1
            }
          }}
        >
          {/* Search on the left */}
          <KnownIssuesSearch
            value={pendingSearchText}
            onChange={e => {
              setPendingSearchText(e.target.value);
              if (e.target.value.trim() === '') {
                setSearchText('');
              }
            }}
            onSearch={(val) => {
              const trimmedVal = val.trim();
              setSearchText(trimmedVal);
              setPendingSearchText(trimmedVal);
              setLiveRegionMessage('')
            }}
            staticContent={staticContent}
          />

          {/* Filter and Sort together on the right */}
           <section aria-label="Sort and filter">
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2.5,
              flexShrink: 0,
              [theme.breakpoints.down('sm')]: {
                flexDirection: 'column',
                gap: 2,
                width: '100%'
              }
            }}
          >
            <KnownIssuesFilterDropdown
              selectedFilters={selectedFilters}
              setSelectedFilters={setSelectedFilters}
              affectedApis={staticContent.affectedApisDropdown?.items || []}
              status={Object.values(staticContent.statuses)}
              environments={Object.values(staticContent.environments || {})}
              staticContent={staticContent}
              setDropdown = {setDropdownHeight}
            />
            <KnownIssuesSortDropdown
              selectedSort={selectedSort}
              onSortChange={handleSortChange}
              staticContent={staticContent}
            />
          </Box>
          </section>
        </Box>
        
        <section aria-label="Search Results">
        {(searchText.trim() || selectedFilters.affectedApis.some(api => api !== staticContent.showAll) ||
          selectedFilters.status.some(status =>  !defaultFliters.includes(status)) ||
          selectedFilters.environments.some(env => env !== staticContent.showAll)) && (
        <>
          <Typography
            variant="h3"
            sx={{ mt: 2, fontWeight: 600, fontSize: '18px', lineHeight: '24px' }}
          >
            {getFilteredIssues().length === 0
              ? `${staticContent['notFound']}`
              : `${getFilteredIssues().length} ${staticContent['searchResult']}`}
          </Typography>
          {/* Visually hidden live region for screen readers */}
          <div
            aria-live="polite"
            role="status"
            style={{
              position: 'absolute',
              width: '1px',
              height: '1px',
              margin: '-1px',
              border: 0,
              padding: 0,
              overflow: 'hidden',
              clip: 'rect(0 0 0 0)',
              clipPath: 'inset(50%)',
              whiteSpace: 'nowrap'
            }}
          >
            {liveRegionMessage}
          </div>
        </>
        )}
        <Box minHeight={dropdownHeight?dropdownHeight:0}>
        {getFilteredIssues().map((issue, index) => {
          return (
            <Issue
              key={index}
              issue={issue}
              issuesIndex={index}
              staticContent={staticContent}
              issueTitl={removePTags(issue.summary)}
            />
          );
        })}
        </Box>
      </section>
      </Box>
      <Box className="banner">
        <div
          dangerouslySetInnerHTML={{
            __html: linkText
          }}
          onClick={handleAnchorClick}
        />
      </Box>
     </main>
    </Box>
  );
};

export default KnownIssues;
